import type { LensType } from "@repo/fika-blog/src/extensions/lenses"
import {
  type Principle,
  type ProofreadDecoration,
  proofreadKey,
} from "@repo/fika-blog/src/extensions/proofread"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import { type AccentHue, colorize } from "@repo/fika-design-system/color"
import { assignInlineVars } from "@vanilla-extract/dynamic"
import {
  type Accessor,
  createMemo,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js"
import { createEditorTransaction } from "solid-tiptap"
import { urqlClient } from "@/context/graphql-provider"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Form } from "@/designSystem/components/form"
import { Icon } from "@/designSystem/components/icon"
import { Text } from "@/designSystem/components/text"
import { Well } from "@/designSystem/components/well"
import { RightPanel } from "@/designSystem/patterns/right-panel"
import { Section } from "@/designSystem/patterns/section"
import { graphql, type VariablesOf } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import type { TranslationFunctions } from "@/i18n/i18n-types"
import { createMutationWithErrorHandling } from "@/lib/graphql-mutation-helpers"
import { createBlogRouter } from "@/routes/posts-router"
import {
  chunkByParagraphs,
  ProofreadMutation,
  type ProofreadProgress,
  proofreadForPrinciple,
} from "./editor/proofread"
import { type EditorPost, usePostEditor } from "./post-editor-context"
import {
  hiddenRadioClass,
  legendClass,
  legendItemClass,
  legendSwatchClass,
  lensOptionClass,
  lensPickerClass,
  navButtonClass,
  principleImageHoverVar,
  principleImageVar,
  principleOptionClass,
  principlePickerClass,
  principleStatusImageClass,
} from "./post-proofread.css"
import proofFrog from "./proof-frog.webp"
import proofFrogHover from "./proof-frog-hover.webp"
import proofRabbit from "./proof-rabbit.webp"
import proofRabbitHover from "./proof-rabbit-hover.webp"
import proofWolf from "./proof-wolf.webp"
import proofWolfHover from "./proof-wolf-hover.webp"

interface PrincipleConfig {
  value: Principle
  hue: AccentHue
  image: string
  imageHover: string
}

const PROOFREAD_PRINCIPLES: Record<Principle, PrincipleConfig> = {
  grammar: {
    value: "grammar",
    hue: "emerald",
    image: proofFrog,
    imageHover: proofFrogHover,
  },
  tighten: {
    value: "tighten",
    hue: "orange",
    image: proofRabbit,
    imageHover: proofRabbitHover,
  },
  refine: {
    value: "refine",
    hue: "indigo",
    image: proofWolf,
    imageHover: proofWolfHover,
  },
}

const PRINCIPLE_LIST: Principle[] = ["grammar", "tighten", "refine"]

export function PostProofReader() {
  const { post, editor } = usePostEditor()
  const { LL } = useI18nContext()

  return (
    <Show when={post()} fallback={<div>{LL().common.loading()}</div>}>
      {post => <SafePostProofReader post={post()} editor={editor()} />}
    </Show>
  )
}

type Props = {
  post: EditorPost
  editor: Editor | undefined
}

function SafePostProofReader(props: Props) {
  const { paths } = createBlogRouter()
  const { LL } = useI18nContext()
  const { activeLocale } = usePostEditor()

  const [selectedPrinciple, setSelectedPrinciple] =
    createSignal<Principle>("grammar")
  const [status, setStatus] = createSignal<"idle" | "proofing" | "done">("idle")
  const [progressInfo, setProgressInfo] = createSignal<{
    completed: number
    total: number
  } | null>(null)
  const [currentIndex, setCurrentIndex] = createSignal(0)
  const [activeLens, setActiveLens] = createSignal<LensType>(null)

  const decorations = createEditorTransaction(
    () => props.editor,
    editor => {
      if (!editor) return []
      const pluginState = proofreadKey.getState(editor.state) as
        | { storedDecorations: ProofreadDecoration[] }
        | undefined
      return pluginState?.storedDecorations ?? []
    },
  )

  const sortedDecorations = createMemo(() =>
    [...decorations()].sort((a, b) => a.from - b.from),
  )

  const fixesCount = createMemo(() => sortedDecorations().length)

  function focusDecoration(index: number) {
    const editor = props.editor
    const decs = sortedDecorations()
    if (!editor || index < 0 || index >= decs.length) return

    const deco = decs[index]
    if (!deco) return

    editor.chain().focus().setTextSelection(deco.from).run()
  }

  function acceptAll() {
    const editor = props.editor
    if (!editor) return

    const reversedDecorations = [...sortedDecorations()].reverse()

    for (const deco of reversedDecorations) {
      editor
        .chain()
        .setTextSelection({ from: deco.from, to: deco.to })
        .insertContent(deco.modifiedText)
        .run()
    }

    editor.chain().clearProofreadDecorations().run()
    setStatus("idle")
  }

  function discard() {
    const editor = props.editor
    if (!editor) return

    editor.chain().clearProofreadDecorations().run()
    setStatus("idle")
  }

  function clearLens() {
    const editor = props.editor
    if (!editor) return

    setActiveLens(null)
    editor.chain().clearLens().run()
  }

  function onLensChange(lens: LensType) {
    const editor = props.editor
    if (!editor) return

    setActiveLens(lens)

    if (lens) {
      editor.chain().setLens(lens).run()
    } else {
      editor.chain().clearLens().run()
    }
  }

  function onProgress(progressData: ProofreadProgress) {
    const editor = props.editor
    if (!editor) return

    setProgressInfo({
      completed: progressData.completed,
      total: progressData.total,
    })

    if (progressData.decorations.length > 0) {
      editor.chain().setProofreadDecorations(progressData.decorations).run()
    }
  }

  const [, executeProofread] = createMutationWithErrorHandling(
    ProofreadMutation,
    "proofread",
  )

  async function executeMutation(
    input: VariablesOf<typeof ProofreadMutation>["input"],
  ) {
    const result = await executeProofread({ input })
    if (!result.success) {
      throw result.error
    }
    return result.data.text
  }

  async function onProofread() {
    const editor = props.editor
    if (!editor) return

    const chunks = chunkByParagraphs(editor.getJSON())
    if (!chunks.length) return

    setStatus("proofing")
    setProgressInfo(null)
    clearLens()
    editor.chain().clearProofreadDecorations().run()

    try {
      const fixesCount = await proofreadForPrinciple({
        postId: props.post.id,
        editor,
        chunks,
        principle: selectedPrinciple(),
        locale: activeLocale() ?? undefined,
        onProgress,
        executeMutation,
      })

      if (fixesCount > 0) {
        setCurrentIndex(0)
        focusDecoration(0)
      }
      setStatus("done")

      const RefetchProofreaderUsageQuery = graphql(`
        query RefetchProofreaderUsage($id: String!) {
          post(id: $id) {
            id
            usedProofreaderAt
          }
        }
      `)
      await urqlClient
        .query(RefetchProofreaderUsageQuery, { id: props.post.id })
        .toPromise()
    } catch (error) {
      console.error("Proofread error:", error)
      setStatus("idle")
    }
  }

  onMount(() => {
    const decs = sortedDecorations()
    if (decs.length > 0) {
      setStatus("done")
      setCurrentIndex(0)
      focusDecoration(0)
    }
  })

  onCleanup(() => {
    clearLens()
  })

  return (
    <RightPanel
      closeHref={paths().post().url}
      header={{
        type: "title",
        title: LL().proofreader.title(),
      }}
    >
      <Show
        when={status() === "idle"}
        fallback={
          <ProofingStatus
            status={status}
            selectedPrinciple={selectedPrinciple()}
            fixesCount={fixesCount}
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
            progressInfo={progressInfo}
            onGoBack={() => setStatus("idle")}
            onDiscard={discard}
            onAcceptAll={acceptAll}
            onFocusDecoration={focusDecoration}
          />
        }
      >
        <PrinciplePicker
          selectedPrinciple={selectedPrinciple()}
          setSelectedPrinciple={setSelectedPrinciple}
          onProofread={onProofread}
        />
      </Show>

      <LensesSection activeLens={activeLens} onLensChange={onLensChange} />
    </RightPanel>
  )
}

type PrinciplePickerProps = {
  selectedPrinciple: Principle
  setSelectedPrinciple: (principle: Principle) => void
  onProofread: () => void
}
type PrincipleOptionProps = {
  principle: Principle
  selectedPrinciple: Principle
  setSelectedPrinciple: (principle: Principle) => void
}

function PrincipleOption(props: PrincipleOptionProps) {
  const { LL } = useI18nContext()

  const config = createMemo(() => PROOFREAD_PRINCIPLES[props.principle])
  const isSelected = createMemo(
    () => props.selectedPrinciple === props.principle,
  )

  return (
    <label
      class={principleOptionClass({ selected: isSelected() })}
      style={assignInlineVars({
        [principleImageVar]: `url(${config().image})`,
        [principleImageHoverVar]: `url(${config().imageHover})`,
      })}
      aria-label={getPrincipleLabel(LL(), props.principle)}
    >
      <input
        type="radio"
        name="principle"
        value={props.principle}
        checked={isSelected()}
        onChange={() => props.setSelectedPrinciple(props.principle)}
        class={hiddenRadioClass}
      />
    </label>
  )
}

function PrinciplePicker(props: PrinciplePickerProps) {
  const { LL } = useI18nContext()

  const principleConfig = createMemo(
    () => PROOFREAD_PRINCIPLES[props.selectedPrinciple],
  )

  return (
    <Form
      title={LL().proofreader.editorTitle()}
      description={LL().proofreader.editorDescription()}
      hue="amber"
      iconName="hat-glasses"
      onSubmit={props.onProofread}
    >
      <Box gap="16">
        <div class={principlePickerClass}>
          <For each={PRINCIPLE_LIST}>
            {principle => (
              <PrincipleOption
                principle={principle}
                selectedPrinciple={props.selectedPrinciple}
                setSelectedPrinciple={props.setSelectedPrinciple}
              />
            )}
          </For>
        </div>
        <Box alignItems="center">
          <Text
            fontSize="readable"
            fontWeight="500"
            color={colorize(principleConfig().hue, "700")}
          >
            {getPrincipleLabel(LL(), props.selectedPrinciple)}
          </Text>
          <Text fontSize="m" color={colorize(principleConfig().hue, "900")}>
            {getPrincipleDescription(LL(), props.selectedPrinciple)}
          </Text>
        </Box>
        <Button hue={principleConfig().hue}>
          {getPrincipleAction(LL(), props.selectedPrinciple)}
        </Button>
      </Box>
    </Form>
  )
}

type ProofingStatusProps = {
  status: Accessor<"idle" | "proofing" | "done">
  selectedPrinciple: Principle
  fixesCount: Accessor<number>
  currentIndex: Accessor<number>
  setCurrentIndex: (index: number) => void
  progressInfo: Accessor<{ completed: number; total: number } | null>
  onGoBack: () => void
  onDiscard: () => void
  onAcceptAll: () => void
  onFocusDecoration: (index: number) => void
}

function ProofingStatus(props: ProofingStatusProps) {
  const { LL } = useI18nContext()

  const principleConfig = createMemo(
    () => PROOFREAD_PRINCIPLES[props.selectedPrinciple],
  )

  const progressPercent = createMemo(() => {
    const info = props.progressInfo()
    if (!info || info.total === 0) return 0
    return Math.round((info.completed / info.total) * 100)
  })

  function goToNext() {
    const count = props.fixesCount()
    if (count === 0) return

    const nextIndex = (props.currentIndex() + 1) % count
    props.setCurrentIndex(nextIndex)
    props.onFocusDecoration(nextIndex)
  }

  function goToPrev() {
    const count = props.fixesCount()
    if (count === 0) return

    const prevIndex = (props.currentIndex() - 1 + count) % count
    props.setCurrentIndex(prevIndex)
    props.onFocusDecoration(prevIndex)
  }

  return (
    <Section
      title={LL().proofreader.editorTitle()}
      description={LL().proofreader.editorDescription()}
      hue="amber"
      iconName="hat-glasses"
    >
      <Well>
        <Box gap="12" alignItems="center">
          <div
            class={principleStatusImageClass}
            style={assignInlineVars({
              [principleImageVar]: `url(${principleConfig().imageHover})`,
            })}
            role="img"
            aria-label={getPrincipleLabel(LL(), props.selectedPrinciple)}
          />
          <Show
            when={props.status() === "proofing"}
            fallback={
              <Show
                when={props.fixesCount() > 0}
                fallback={
                  <Box gap="8" alignItems="center">
                    <Text
                      fontSize="readable"
                      fontWeight="500"
                      fontFamily="serif"
                      textAlign="center"
                    >
                      {LL().proofreader.noFixesFound()}
                    </Text>
                    <Button
                      hue="sand"
                      kind="surface"
                      size="s"
                      onClick={props.onGoBack}
                    >
                      {LL().common.back()}
                    </Button>
                  </Box>
                }
              >
                <Box gap="12" alignItems="center">
                  <Text
                    fontSize="readable"
                    fontWeight="500"
                    fontFamily="serif"
                    textAlign="center"
                  >
                    {LL().proofreader.countFixes(props.fixesCount())}
                  </Text>
                  <Box flexDirection="row" gap="8" alignItems="center">
                    <button
                      type="button"
                      class={navButtonClass}
                      onClick={goToPrev}
                      disabled={props.fixesCount() <= 1}
                    >
                      <Icon name="arrow-left" size="18" />
                    </button>
                    <Text fontSize="s" color="sand600">
                      {props.currentIndex() + 1}/{props.fixesCount()}
                    </Text>
                    <button
                      type="button"
                      onClick={goToNext}
                      disabled={props.fixesCount() <= 1}
                      class={navButtonClass}
                    >
                      <Icon name="arrow-right" size="18" />
                    </button>
                  </Box>
                  <Box flexDirection="row" gap="8">
                    <Button
                      hue="sand"
                      kind="surface"
                      size="s"
                      iconName="x"
                      onClick={props.onDiscard}
                    >
                      {LL().proofreader.discard()}
                    </Button>
                    <Button
                      hue={principleConfig().hue}
                      size="s"
                      iconName="check"
                      onClick={props.onAcceptAll}
                    >
                      {LL().proofreader.acceptAll()}
                    </Button>
                  </Box>
                </Box>
              </Show>
            }
          >
            <Text
              fontSize="readable"
              fontWeight="500"
              fontFamily="serif"
              textAlign="center"
              color={colorize(principleConfig().hue, "700")}
            >
              {props.fixesCount() > 0
                ? LL().proofreader.countFixesFound(props.fixesCount())
                : LL().proofreader.scanning()}
            </Text>
            <Text color="sand600" textAlign="center">
              {LL().proofreader.percentComplete({ percent: progressPercent() })}
            </Text>
          </Show>
        </Box>
      </Well>
    </Section>
  )
}

type LensesSectionProps = {
  activeLens: Accessor<LensType>
  onLensChange: (lens: LensType) => void
}

function LensesSection(props: LensesSectionProps) {
  const { LL } = useI18nContext()
  const descriptions = createMemo(
    () => getLensDescription(LL(), props.activeLens())?.split(".") ?? [],
  )

  const legendItems = createMemo(() => {
    const lens = props.activeLens()
    if (!lens) return []

    if (lens === "sentence-difficulty") {
      return [
        {
          color: "orange" as const,
          label: LL().proofreader.lenses.legend.difficulty.orange(),
        },
        {
          color: "amber" as const,
          label: LL().proofreader.lenses.legend.difficulty.amber(),
        },
      ]
    }

    if (lens === "sentence-variation") {
      return [
        {
          color: "orange" as const,
          label: LL().proofreader.lenses.legend.variation.orange(),
        },
        {
          color: "amber" as const,
          label: LL().proofreader.lenses.legend.variation.amber(),
        },
      ]
    }

    return []
  })

  const lensOptions = createMemo(
    () =>
      [
        { value: null, label: LL().proofreader.lenses.off(), icon: "eye-off" },
        {
          value: "sentence-difficulty",
          label: LL().proofreader.lenses.difficulty(),
          icon: "ruler",
        },
        {
          value: "sentence-variation",
          label: LL().proofreader.lenses.variation(),
          icon: "scale",
        },
      ] as const,
  )

  return (
    <Section
      title={LL().proofreader.lenses.title()}
      description={LL().proofreader.lenses.description()}
      hue="amber"
      iconName="glasses"
    >
      <Box gap="16">
        <div class={lensPickerClass}>
          {lensOptions().map(option => (
            <label
              class={lensOptionClass({
                selected: props.activeLens() === option.value,
              })}
            >
              <input
                type="radio"
                name="lens"
                value={option.value ?? ""}
                checked={props.activeLens() === option.value}
                onChange={() => props.onLensChange(option.value)}
                class={hiddenRadioClass}
              />
              <Icon name={option.icon} size="24" />
              {option.label}
            </label>
          ))}
        </div>
        <Show when={props.activeLens()}>
          <Box gap="12">
            <Box>
              <For each={descriptions()}>
                {part => (
                  <Text fontSize="s" color="sand600">
                    {part}.
                  </Text>
                )}
              </For>
            </Box>
            <div class={legendClass}>
              <For each={legendItems()}>
                {item => (
                  <div class={legendItemClass}>
                    <div class={legendSwatchClass({ color: item.color })} />
                    <Text fontSize="s" color="sand700">
                      {item.label}
                    </Text>
                  </div>
                )}
              </For>
            </div>
          </Box>
        </Show>
      </Box>
    </Section>
  )
}

function getLensDescription(
  LL: TranslationFunctions,
  lens: LensType,
): string | null {
  if (lens === "sentence-difficulty")
    return LL.proofreader.lenses.difficultyDescription()
  if (lens === "sentence-variation")
    return LL.proofreader.lenses.variationDescription()
  return null
}

function getPrincipleLabel(LL: TranslationFunctions, principle: Principle) {
  return {
    grammar: LL.proofreader.principles.grammar.label(),
    tighten: LL.proofreader.principles.tighten.label(),
    refine: LL.proofreader.principles.refine.label(),
  }[principle]
}

function getPrincipleDescription(
  LL: TranslationFunctions,
  principle: Principle,
) {
  return {
    grammar: LL.proofreader.principles.grammar.description(),
    tighten: LL.proofreader.principles.tighten.description(),
    refine: LL.proofreader.principles.refine.description(),
  }[principle]
}

function getPrincipleAction(LL: TranslationFunctions, principle: Principle) {
  return {
    grammar: LL.proofreader.principles.grammar.action(),
    tighten: LL.proofreader.principles.tighten.action(),
    refine: LL.proofreader.principles.refine.action(),
  }[principle]
}
